import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";
import ImageUploadField from "../../components/Common/ImageUploadField";
import { IMAGE_SPECS } from "../../utils/imageSpecs";

const PAGE_KEY = "mamangam-hall-booking";

const UPLOAD_FOLDER = "mamangam-hall";

interface PageConfig {
  id: string;
  page_key: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_active: boolean;
}

interface PageSection {
  id: string;
  page_config_id: string;
  slug: string;
  title: string;
  display_order: number;
  is_active: boolean;
  data: {
    icon_svg?: string;
    card_image_url?: string;
    detail_image_url?: string;
    feature_points?: string[];
    whatsapp_number?: string;
    whatsapp_prefilled_text?: string;
  };
}

const MamangamHallBooking: React.FC = () => {
  const [, setConfig] = useState<PageConfig | null>(null);
  const [sections, setSections] = useState<PageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  // Hero config form state
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [heroDescription, setHeroDescription] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Section modal state
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionModalMode, setSectionModalMode] = useState<"add" | "edit">(
    "add",
  );
  const [selectedSection, setSelectedSection] = useState<PageSection | null>(
    null,
  );

  // Section form state
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionSlug, setSectionSlug] = useState("");
  const [sectionIconSvg, setSectionIconSvg] = useState("");
  const [sectionCardImage, setSectionCardImage] = useState("");
  const [sectionDetailImage, setSectionDetailImage] = useState("");
  const [sectionFeaturePoints, setSectionFeaturePoints] = useState<string[]>(
    [],
  );
  const [sectionWhatsappNumber, setSectionWhatsappNumber] = useState("");
  const [sectionWhatsappText, setSectionWhatsappText] = useState("");
  const [sectionDisplayOrder, setSectionDisplayOrder] = useState(1);
  const [sectionIsActive, setSectionIsActive] = useState(true);
  const [savingSection, setSavingSection] = useState(false);

  // Image upload state
  const [uploadingCard, setUploadingCard] = useState(false);
  const [uploadingDetail, setUploadingDetail] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<PageSection | null>(
    null,
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPageConfigByKey(PAGE_KEY);
      if (response.success && response.data) {
        const data = response.data;
        setConfig(data);
        setSections(data.sections || []);

        // Populate hero form
        setHeroTitle(data.title || "");
        setHeroSubtitle(data.subtitle || "");
        setHeroDescription(data.description || "");
        setMetaTitle(data.meta_title || "");
        setMetaDescription(data.meta_description || "");
        setIsActive(Boolean(data.is_active));
      }
    } catch (error: any) {
      console.error("Error loading page config:", error);
      toast.error("Failed to load page configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHeroConfig = async () => {
    try {
      setSavingConfig(true);
      const response = await adminService.updatePageConfig(PAGE_KEY, {
        title: heroTitle,
        subtitle: heroSubtitle,
        description: heroDescription,
        metaTitle: metaTitle,
        metaDescription: metaDescription,
        isActive: isActive,
      });

      if (response.success) {
        toast.success("Hero configuration saved successfully");
        setConfig(response.data);
      } else {
        toast.error(response.message || "Failed to save hero configuration");
      }
    } catch (error: any) {
      console.error("Error saving hero config:", error);
      toast.error("Failed to save hero configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const openAddSectionModal = () => {
    setSectionModalMode("add");
    setSelectedSection(null);
    resetSectionForm();
    setSectionDisplayOrder(sections.length + 1);
    setShowSectionModal(true);
  };

  const openEditSectionModal = (section: PageSection) => {
    setSectionModalMode("edit");
    setSelectedSection(section);
    setSectionTitle(section.title);
    setSectionSlug(section.slug);
    setSectionIconSvg(section.data?.icon_svg || "");
    setSectionCardImage(section.data?.card_image_url || "");
    setSectionDetailImage(section.data?.detail_image_url || "");
    setSectionFeaturePoints(section.data?.feature_points || []);
    setSectionWhatsappNumber(section.data?.whatsapp_number || "");
    setSectionWhatsappText(section.data?.whatsapp_prefilled_text || "");
    setSectionDisplayOrder(section.display_order);
    setSectionIsActive(section.is_active);
    setShowSectionModal(true);
  };

  const resetSectionForm = () => {
    setSectionTitle("");
    setSectionSlug("");
    setSectionIconSvg("");
    setSectionCardImage("");
    setSectionDetailImage("");
    setSectionFeaturePoints([]);
    setSectionWhatsappNumber("");
    setSectionWhatsappText("");
    setSectionIsActive(true);
  };

  const handleSaveSection = async () => {
    if (!sectionTitle.trim()) {
      toast.error("Section title is required");
      return;
    }

    try {
      setSavingSection(true);

      const sectionData = {
        title: sectionTitle,
        slug: sectionSlug || undefined,
        displayOrder: sectionDisplayOrder,
        isActive: sectionIsActive,
        data: {
          icon_svg: sectionIconSvg,
          card_image_url: sectionCardImage,
          detail_image_url: sectionDetailImage,
          feature_points: sectionFeaturePoints.filter((p) => p.trim()),
          whatsapp_number: sectionWhatsappNumber,
          whatsapp_prefilled_text: sectionWhatsappText,
        },
      };

      let response;
      if (sectionModalMode === "add") {
        response = await adminService.createPageSection(PAGE_KEY, sectionData);
      } else {
        if (!selectedSection) return;
        response = await adminService.updatePageSection(
          PAGE_KEY,
          selectedSection.id,
          sectionData,
        );
      }

      if (response.success) {
        toast.success(
          sectionModalMode === "add"
            ? "Section created successfully"
            : "Section updated successfully",
        );
        setShowSectionModal(false);
        loadData();
      } else {
        toast.error(response.message || "Failed to save section");
      }
    } catch (error: any) {
      console.error("Error saving section:", error);
      toast.error("Failed to save section");
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!sectionToDelete) return;

    try {
      const response = await adminService.deletePageSection(
        PAGE_KEY,
        sectionToDelete.id,
      );
      if (response.success) {
        toast.success("Section deleted successfully");
        setShowDeleteModal(false);
        setSectionToDelete(null);
        loadData();
      } else {
        toast.error(response.message || "Failed to delete section");
      }
    } catch (error: any) {
      console.error("Error deleting section:", error);
      toast.error("Failed to delete section");
    }
  };

  const handleAddFeaturePoint = () => {
    setSectionFeaturePoints([...sectionFeaturePoints, ""]);
  };

  const handleRemoveFeaturePoint = (index: number) => {
    setSectionFeaturePoints(sectionFeaturePoints.filter((_, i) => i !== index));
  };

  const handleFeaturePointChange = (index: number, value: string) => {
    const updated = [...sectionFeaturePoints];
    updated[index] = value;
    setSectionFeaturePoints(updated);
  };

  const uploadPageAsset = async (file: File): Promise<string | null> => {
    const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
    if (response.success && response.data) {
      return response.data.url || response.data.filename;
    }
    throw new Error(response.error || "Upload failed");
  };

  const handleCardUpload = async (file: File): Promise<string | null> => {
    setUploadingCard(true);
    try {
      return await uploadPageAsset(file);
    } finally {
      setUploadingCard(false);
    }
  };

  const handleDetailUpload = async (file: File): Promise<string | null> => {
    setUploadingDetail(true);
    try {
      return await uploadPageAsset(file);
    } finally {
      setUploadingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mamangam Hall Booking - Admin</title>
      </Helmet>

      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Mamangam Hall Booking</h2>
            <p className="text-muted mb-0">
              Manage content, images, and WhatsApp contact for the Mamangam Hall
              Booking page
            </p>
          </div>
        </div>

        {/* Hero Section Config */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-house-door me-2"></i>
              Hero Section Configuration
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Hero Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={heroTitle}
                    onChange={(e) => setHeroTitle(e.target.value)}
                    placeholder="Mamangam Hall Booking"
                  />
                </Form.Group>
              </Col>
              {/* <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hero Subtitle</Form.Label>
                  <Form.Control
                    type="text"
                    value={heroSubtitle}
                    onChange={(e) => setHeroSubtitle(e.target.value)}
                    placeholder="Mamangam Hall"
                  />
                </Form.Group>
              </Col> */}
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Hero Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={heroDescription}
                onChange={(e) => setHeroDescription(e.target.value)}
                placeholder="Mamangam Hall at MY Cinemas is a premium multi-purpose space..."
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Title (SEO)</Form.Label>
                  <Form.Control
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder="Mamangam Hall Booking - MY Cinemas"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Description (SEO)</Form.Label>
                  <Form.Control
                    type="text"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Book Mamangam Hall at MY Cinemas..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is-active-switch"
                label="Page is active (visible on website)"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Form.Group>

            <Button
              variant="primary"
              onClick={handleSaveHeroConfig}
              disabled={savingConfig}
            >
              {savingConfig ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Save Hero Configuration
                </>
              )}
            </Button>
          </Card.Body>
        </Card>

        {/* Categories Section */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-grid-3x3-gap me-2"></i>
              Categories ({sections.length})
            </h5>
            <Button variant="success" onClick={openAddSectionModal}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Category
            </Button>
          </Card.Header>
          <Card.Body>
            {sections.length === 0 ? (
              <Alert variant="info">
                No categories yet. Click "Add Category" to create your first
                one.
              </Alert>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>Title</th>
                    <th>Slug</th>
                    <th>WhatsApp</th>
                    <th>Status</th>
                    <th style={{ width: "150px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section, index) => (
                    <tr key={section.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{section.title}</strong>
                      </td>
                      <td>
                        <code>{section.slug}</code>
                      </td>
                      <td>
                        {section.data?.whatsapp_number ? (
                          <Badge bg="success">
                            <i className="bi bi-whatsapp me-1"></i>
                            {section.data.whatsapp_number}
                          </Badge>
                        ) : (
                          <Badge bg="secondary">Not set</Badge>
                        )}
                      </td>
                      <td>
                        {section.is_active ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openEditSectionModal(section)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setSectionToDelete(section);
                            setShowDeleteModal(true);
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Section Edit Modal */}
      <Modal
        show={showSectionModal}
        onHide={() => setShowSectionModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {sectionModalMode === "add" ? "Add Category" : "Edit Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                  placeholder="Private Screening Space"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Slug</Form.Label>
                <Form.Control
                  type="text"
                  value={sectionSlug}
                  onChange={(e) => setSectionSlug(e.target.value)}
                  placeholder="private-screening-space"
                />
                <Form.Text className="text-muted">
                  Auto-generated from title if empty
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Icon SVG</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={sectionIconSvg}
              onChange={(e) => setSectionIconSvg(e.target.value)}
              placeholder="<svg width='20' height='20'>...</svg>"
            />
            <Form.Text className="text-muted">
              Paste the SVG code for the category icon
            </Form.Text>
          </Form.Group>

          {/* Card Image (Vertical) */}
          <Form.Group className="mb-3">
            <Form.Label>
              Card Image ({IMAGE_SPECS.mamangamCard.spec.aspectRatio}) - Vertical
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={sectionCardImage}
                onChange={(e) => setSectionCardImage(e.target.value)}
                placeholder="https://cdn.example.com/mamangalam-1.png"
              />
              <ImageUploadField
                spec={IMAGE_SPECS.mamangamCard}
                onUpload={handleCardUpload}
                onChange={(url) => setSectionCardImage(url)}
                disabled={uploadingCard}
                buttonVariant="outline-secondary"
                buttonLabel="Upload"
                showPreview={false}
              />
            </InputGroup>
            {sectionCardImage && (
              <div className="mt-2">
                <img
                  src={sectionCardImage}
                  alt="Card preview"
                  style={{
                    maxWidth: "100px",
                    maxHeight: "180px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>
            )}
          </Form.Group>

          {/* Detail Image (Horizontal) */}
          <Form.Group className="mb-3">
            <Form.Label>
              Detail Image ({IMAGE_SPECS.mamangamDetail.spec.aspectRatio}) - Horizontal
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={sectionDetailImage}
                onChange={(e) => setSectionDetailImage(e.target.value)}
                placeholder="https://cdn.example.com/detaild-full.png"
              />
              <ImageUploadField
                spec={IMAGE_SPECS.mamangamDetail}
                onUpload={handleDetailUpload}
                onChange={(url) => setSectionDetailImage(url)}
                disabled={uploadingDetail}
                buttonVariant="outline-secondary"
                buttonLabel="Upload"
                showPreview={false}
              />
            </InputGroup>
            {sectionDetailImage && (
              <div className="mt-2">
                <img
                  src={sectionDetailImage}
                  alt="Detail preview"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "100px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>
            )}
          </Form.Group>

          {/* Feature Points */}
          <Form.Group className="mb-3">
            <Form.Label>Feature Points</Form.Label>
            {sectionFeaturePoints.map((point, index) => (
              <InputGroup className="mb-2" key={index}>
                <Form.Control
                  type="text"
                  value={point}
                  onChange={(e) =>
                    handleFeaturePointChange(index, e.target.value)
                  }
                  placeholder={`Feature point ${index + 1}`}
                />
                <Button
                  variant="outline-danger"
                  onClick={() => handleRemoveFeaturePoint(index)}
                >
                  <i className="bi bi-x"></i>
                </Button>
              </InputGroup>
            ))}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleAddFeaturePoint}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Feature Point
            </Button>
          </Form.Group>

          {/* WhatsApp */}
          <Card className="mb-3 bg-light">
            <Card.Body>
              <h6 className="mb-3">
                <i className="bi bi-whatsapp me-2 text-success"></i>
                WhatsApp Contact Button
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>WhatsApp Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={sectionWhatsappNumber}
                      onChange={(e) => setSectionWhatsappNumber(e.target.value)}
                      placeholder="+918484123456"
                    />
                    <Form.Text className="text-muted">
                      Include country code (e.g., +91)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prefilled Text</Form.Label>
                    <Form.Control
                      type="text"
                      value={sectionWhatsappText}
                      onChange={(e) => setSectionWhatsappText(e.target.value)}
                      placeholder="Hi, I am interested in your Private Screening Space package"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  value={sectionDisplayOrder}
                  onChange={(e) =>
                    setSectionDisplayOrder(parseInt(e.target.value) || 1)
                  }
                  min={1}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Check
                  type="switch"
                  id="section-active-switch"
                  label="Active (visible on website)"
                  checked={sectionIsActive}
                  onChange={(e) => setSectionIsActive(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSectionModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSection}
            disabled={savingSection}
          >
            {savingSection ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-save me-2"></i>
                {sectionModalMode === "add" ? "Create" : "Update"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the category{" "}
          <strong>{sectionToDelete?.title}</strong>? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteSection}>
            <i className="bi bi-trash me-2"></i>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MamangamHallBooking;
